// Module-level registry that lets multiple <video> elements (tile + modal
// hero) bind to the same WHEP MediaStream for a given doorbot_id, so the
// session is established exactly once and there's no visual flicker when
// the operator opens / closes the focus modal.
//
// The publisher is `<LiveTileVideo>` — it calls publishStream() when a
// session lands, and clears the slot when it tears down. Subscribers
// (`useSharedLiveStream` hook) read the current stream and re-render
// when the publisher swaps it (rotation, reconnect).

type Listener = (stream: MediaStream | null) => void;

interface Entry {
  stream: MediaStream | null;
  listeners: Set<Listener>;
}

const entries = new Map<string, Entry>();

function ensureEntry(deviceId: string): Entry {
  let entry = entries.get(deviceId);
  if (!entry) {
    entry = { stream: null, listeners: new Set() };
    entries.set(deviceId, entry);
  }
  return entry;
}

export function publishStream(deviceId: string, stream: MediaStream | null): void {
  const entry = ensureEntry(deviceId);
  if (entry.stream === stream) return;
  entry.stream = stream;
  for (const fn of entry.listeners) {
    try {
      fn(stream);
    } catch {
      /* listener errors don't kill the publisher */
    }
  }
}

export function getStream(deviceId: string): MediaStream | null {
  return entries.get(deviceId)?.stream ?? null;
}

export function subscribe(deviceId: string, fn: Listener): () => void {
  const entry = ensureEntry(deviceId);
  entry.listeners.add(fn);
  return (): void => {
    entry.listeners.delete(fn);
  };
}
